import { AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { ITEM_REVENUE_CATEGORY } from 'src/app/helpers/item-revenue.helper';
import { LENS_OPTIONS, LENS_OPTIONS_TYPES } from 'src/app/helpers/lens-options.helper';
import { LENS, LENS_TYPES } from 'src/app/helpers/lenses.helper';
import { ORDER_CATEGORY } from 'src/app/helpers/order-categories.helper';
import { CompanyLenses } from 'src/app/models/company-lenses.model';
import { ProductDataSubject } from 'src/app/models/product-data-subject.model';
import { CartItemsRequest } from 'src/app/models/requests/cart-items-request.model';
import { ItemDetailsRequest } from 'src/app/models/requests/item-details-request.model';
import { CartManagerService } from 'src/app/services/local/cart-manager.service';
import { OrderManagerService } from 'src/app/services/local/order-manager.service';
import { CompanyComputerLensesService } from 'src/app/services/repositories/company-computer-lenses.service';
import { CompanyDressLensesService } from 'src/app/services/repositories/company-dress-lenses.service';
import { CompanyLensesService } from 'src/app/services/repositories/company-lenses.service';
import { CompanyRespiratorLensesService } from 'src/app/services/repositories/company-respirator-lenses.service';

@Component({
    selector: 'app-bifocal-progressive',
    templateUrl: './bifocal-progressive.component.html',
    styleUrls: ['./bifocal-progressive.component.scss'],
    standalone: false
})
export class BifocalProgressiveComponent implements OnInit, AfterViewInit {
  toggleClass: boolean = true;
  selectedLens: CompanyLenses | undefined;
  bifocalLenses: CompanyLenses[] = [];
  progressiveLenses: CompanyLenses[] = [];
  companyLenses: CompanyLenses[] = [];
  currentParentClass = "lens-module";
  productNameParam: string | null | undefined;
  tempCartEventsSubject: Subject<ProductDataSubject> = new Subject<ProductDataSubject>();
  isEditMode: boolean = false;
  queryParams: {} = {};

  @ViewChildren('lens') lensElements!: QueryList<any>;

  constructor(private _router: Router,
              private _route: ActivatedRoute,
              private _cartManagerService: CartManagerService,
              private _companyLensesService: CompanyLensesService,
              private _companyComputerLensesService: CompanyComputerLensesService,
              private _companyDressLensesService: CompanyDressLensesService,
              private _companyRespiratorLensesService: CompanyRespiratorLensesService,
              public _orderManagerService: OrderManagerService,
              private _changeDetectionRef: ChangeDetectorRef) { }

  ngOnInit(): void {
    this._route.queryParams
    .subscribe(params => {
      this.isEditMode = params.isEditMode === 'true';
      this.queryParams = this.isEditMode ? {'isEditMode' : this.isEditMode} : {};
      this.getBifocalProgressiveLenses();
    });
  }
  
  ngAfterViewInit() {
    this.lensElements.changes.subscribe(ql => {
      this._orderManagerService
        .preSelectLensItems(ql, this.isEditMode, this.companyLenses);
      // manually detect changes is needed when onclick is submitted programatically
      this._changeDetectionRef.detectChanges();
		});
  }

  getBifocalProgressiveLenses() {
    const cartItem = this._cartManagerService.getTempCartItem();
    let lensObservable: any;
    if (cartItem) {
      switch (cartItem?.itemCategory) {
        case ORDER_CATEGORY.glasses:
          lensObservable = this._companyLensesService.getLenses(LENS_TYPES.bifocalProg);
          break;
        case ORDER_CATEGORY.computerGlasses:
          lensObservable = this._companyComputerLensesService.getLenses(LENS_TYPES.bifocalProg);
          break;
        case ORDER_CATEGORY.dressGlasses:
          lensObservable = this._companyDressLensesService.getLenses(LENS_TYPES.bifocalProg);
          break;
        case ORDER_CATEGORY.respiratorInserts:
          lensObservable = this._companyRespiratorLensesService.getLenses(LENS_TYPES.bifocalProg);
          break;
        default:
          this.redirectToDashboard();
      }
      lensObservable.subscribe((data: CompanyLenses[]) => {
        if (data) {
         this.populateLensData(data);
        }
      }, (error: any) => {
        console.log(error);
      });
    }
    else {
      this.redirectToDashboard();
    }
  }

  redirectToDashboard() {
    this._router.navigate(['/dashboard']);
  }

  populateLensData(data: CompanyLenses[]) {
     this.companyLenses = data;
     this.splitByLensCategory();
  }

  selectLens(lensId: string) {
    this.currentParentClass = `lens${lensId} selected`;
    const lensIdNumber: number = parseInt(lensId);
    this.selectedLens = this.companyLenses.filter(cl => cl.lensId == lensIdNumber)[0];
    this.checkLensRules(lensIdNumber);
    this.saveLensSelection();
  }

  checkLensRules(lensId: number) {
    // check lens rules incase user has gone back to switch. Different lens options might need to be applied.
    if (lensId !== LENS.basicProg) {
      // check temp cart for blue right and remove item - since its not allowed
      const cart = this._cartManagerService.getTempCartItem();
      if (cart) {
        this.basicProgRule(cart);
      }
    }
  }

  basicProgRule(cart: CartItemsRequest) {
    const blueLight = cart.itemDetails.find(details =>
      details.itemType == ITEM_REVENUE_CATEGORY.lensTreatment &&
      details.id  == LENS_OPTIONS.blueLight);
      if (blueLight) {
        this._cartManagerService.removeTempCartItemDetail(ITEM_REVENUE_CATEGORY.lensTreatment, LENS_OPTIONS_TYPES.scratch);
      }
  }

  saveLensSelection() {
    if (this.selectedLens) {
      const cartItemDetail : ItemDetailsRequest = {
        id: this.selectedLens.lensId,
        itemType: ITEM_REVENUE_CATEGORY.lenses,
        itemDescription: this.selectedLens.lensDescription
      };
      this._cartManagerService.updateTempCartItemDetail(cartItemDetail);
      this.emitChangeToProductData();
    }
  }

  splitByLensCategory() {
    if (this.companyLenses.length > 0) {
      this.bifocalLenses = this.companyLenses.filter(cl => cl.visionType == LENS_TYPES.bifocal);
      this.progressiveLenses = this.companyLenses.filter(cl => cl.visionType == LENS_TYPES.progressive);
    }
  }

  back() {
    this._cartManagerService.removeTempCartItemDetailByItemType(ITEM_REVENUE_CATEGORY.lenses);
    this._router.navigate(['/lens-selection/prescription'], { queryParams: this.queryParams });
  }

  emitChangeToProductData() {
    let pds: ProductDataSubject = { status: 'lens-update' };
    this.tempCartEventsSubject.next(pds);
  }

}


